import dayjs from 'dayjs';

import ListView from '@ingka/list-view';
import Select, { Option } from '@ingka/select';
import Text from '@ingka/text';
import { selectBuCode } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectPaymentDetails } from '@repo/api-adapters/selectors/payment.selectors';
import { useStoreDetails } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { paymentMethods } from '@repo/types/asset-payment';
import { DayPicker } from '@repo/ui/daypicker';
import { formatDateWithWeekday, resolveTimezone } from '@repo/utils';

import { getServiceEndpoint } from '@/api/helpers.api.ts';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks.ts';
import { getDatesInRange, getDayBeforeOrderEarliestDeliveryDate } from '@/utils/asset-payment-date.utils.ts';
import { getAppConfig } from '@/utils/window.utils.ts';

import styles from '@/components/modals/asset-payment-modal/styles.module.scss';

interface Props {
    setSelectedPaymentDate: (date: string) => void;
}

export const AssetPaymentEdit = ({ setSelectedPaymentDate }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const { paymentDueDate } = selectPaymentDetails(data);

    const assetBuCode = selectBuCode(data) ?? '';
    const config = getAppConfig();
    const storeDetailsBaseUrl = getServiceEndpoint(config.api.services.classicSalja, 'storeDetails');
    const { data: storeDetails } = useStoreDetails(storeDetailsBaseUrl, assetBuCode);
    const storeTimezone = storeDetails?.TimeZone ?? 'UTC';
    const resolvedTimezone = resolveTimezone(storeTimezone);
    const dateFormat = config.dateTimeFormat;
    const orderPaymentBeginDate = dayjs().tz(resolvedTimezone).startOf('day').toDate();
    const orderEarliestDeliveryDate =
        data?.assetType === 'order'
            ? getDayBeforeOrderEarliestDeliveryDate(data, storeTimezone)
            : orderPaymentBeginDate;
    const initialPaymentDueDate = paymentDueDate ? dayjs.utc(paymentDueDate).tz(resolvedTimezone).toDate() : undefined;

    const formattedPaymentDueDate = paymentDueDate
        ? formatDateWithWeekday(paymentDueDate, dateFormat, storeTimezone)
        : '';
    const availableDates = getDatesInRange(
        dayjs.tz(orderPaymentBeginDate, resolvedTimezone),
        dayjs.tz(orderEarliestDeliveryDate, resolvedTimezone)
    );

    const handleDayChange = (date: Date) => {
        setSelectedPaymentDate(dayjs.tz(date, resolvedTimezone).endOf('day').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'));
    };

    return (
        <ListView id="asset-payment-edit-view" data-testid="asset-payment-edit-view">
            <Select
                id="asset-payment-method-select"
                label={translate('asset_payment_modal_payment_method')}
                defaultValue={paymentMethods[0]}
            >
                {paymentMethods.map((paymentMethod) => (
                    <Option
                        key={paymentMethod}
                        value={paymentMethod}
                        name={translate(`asset_payment_modal_payment_method_${paymentMethod.toLowerCase()}`)}
                    />
                ))}
            </Select>
            <hr className={`${styles.divider}`} />
            <Text
                textStyle="Heading.XS"
                id="payment-method-calendar"
                data-testid="test-payment-method-calendar"
                className={`${styles.asset_payment_settings_text}`}
            >
                {`${translate('asset_payment_modal_pay_before')} ${formattedPaymentDueDate}`}
            </Text>
            <DayPicker
                {...(initialPaymentDueDate ? { initialDate: initialPaymentDueDate } : {})}
                onDayChange={handleDayChange}
                disabledDates={[
                    { before: orderPaymentBeginDate },
                    { after: orderEarliestDeliveryDate ?? orderPaymentBeginDate },
                ]}
                availableDates={availableDates}
                translate={translate}
                timeZone={storeTimezone}
            />
        </ListView>
    );
};
